/**
 * Time-Varying Correlation Model using DCC-GARCH
 * Dynamic Conditional Correlation with GARCH(1,1) volatility modeling
 * 
 * References:
 * - Engle (2002) - Dynamic Conditional Correlation
 * - Bollerslev (1986) - GARCH models
 */

import type {
  CorrelationForecast,
  TimeVaryingCorrelation,
  TimeVaryingCorrelationOptions,
  CorrelationHistoryPoint
} from '@/types/timeVaryingCorrelation';
import { GARCHParameterEstimator, type GARCHParameters } from './GARCHParameterEstimation';

interface GARCHResults {
  current_volatility: number;
  forecast_volatility: (periods: number) => number;
  long_term_volatility: number;
}

interface StatisticalMetrics {
  long_term_mean: number;
  correlation_volatility: number;
  min: number;
  max: number;
  median: number;
}

export class TimeVaryingCorrelationModel {
  // Mean reversion parameters
  private meanReversionSpeed = 0.15; // 15% per month (~2.4 months half-life)
  
  // GARCH parameter estimator
  private garchEstimator = new GARCHParameterEstimator();
  
  /**
   * Calculate time-varying correlation with full GARCH modeling
   */
  public calculateTimeVaryingCorrelation(
    returns1: number[],
    returns2: number[],
    dates: string[],
    options: TimeVaryingCorrelationOptions = {}
  ): TimeVaryingCorrelation {
    const windowSize = options.windowSize || 252;
    const includeForecasts = options.includeForecasts ?? true;
    
    // Validate inputs
    if (returns1.length !== returns2.length || returns1.length !== dates.length) {
      throw new Error('Input arrays must have equal length');
    }
    
    if (returns1.length < windowSize + 50) {
      throw new Error(`Insufficient data: need at least ${windowSize + 50} observations`);
    }
    
    // Step 1: Calculate rolling correlations
    const rollingData = this.calculateRollingCorrelations(
      returns1,
      returns2,
      dates,
      windowSize
    );
    
    // Step 2: Fit GARCH model to correlation series
    const garchResults = this.fitGARCH(rollingData.correlations);
    
    // Step 3: Calculate statistical metrics
    const stats = this.calculateStatistics(rollingData.correlations);
    
    // Step 4: Classify current regime
    const regime = this.classifyVolatilityRegime(
      garchResults.current_volatility,
      stats.correlation_volatility
    );
    
    // Step 5: Estimate breakdown probability
    const breakdownProb = this.estimateBreakdownProbability(
      rollingData.current,
      garchResults.current_volatility,
      stats.long_term_mean
    );
    
    // Step 6: Generate forecasts
    const forecasts = includeForecasts ? {
      forecast_1m: this.forecastCorrelation(
        rollingData.current,
        stats.long_term_mean,
        21,  // ~1 month
        garchResults
      ),
      forecast_3m: this.forecastCorrelation(
        rollingData.current,
        stats.long_term_mean,
        63,  // ~3 months
        garchResults
      ),
      forecast_6m: this.forecastCorrelation(
        rollingData.current,
        stats.long_term_mean,
        126, // ~6 months
        garchResults
      ),
      forecast_1y: this.forecastCorrelation(
        rollingData.current,
        stats.long_term_mean,
        252, // ~1 year
        garchResults
      )
    } : {
      forecast_1m: { point: rollingData.current, lower: rollingData.current, upper: rollingData.current },
      forecast_3m: { point: rollingData.current, lower: rollingData.current, upper: rollingData.current },
      forecast_6m: { point: rollingData.current, lower: rollingData.current, upper: rollingData.current },
      forecast_1y: { point: rollingData.current, lower: rollingData.current, upper: rollingData.current }
    };
    
    // Step 7: Generate insights
    const { interpretation, warnings, recommendations } = this.generateInsights(
      rollingData.current,
      stats,
      regime,
      breakdownProb,
      forecasts
    );
    
    // Step 8: Calculate mean reversion metrics
    const halfLife = this.calculateHalfLife(this.meanReversionSpeed);
    
    return {
      current: rollingData.current,
      current_date: rollingData.current_date,
      ...forecasts,
      correlation_volatility: stats.correlation_volatility,
      volatility_regime: regime,
      long_term_mean: stats.long_term_mean,
      percentile_rank: this.calculatePercentileRank(
        rollingData.current,
        rollingData.correlations
      ),
      breakdown_probability: breakdownProb,
      mean_reversion_strength: this.meanReversionSpeed,
      half_life_days: halfLife,
      history: rollingData.history,
      interpretation,
      warnings,
      recommendations
    };
  }
  
  /**
   * Calculate rolling window correlations
   */
  private calculateRollingCorrelations(
    returns1: number[],
    returns2: number[],
    dates: string[],
    windowSize: number
  ): {
    correlations: number[];
    dates: string[];
    current: number;
    current_date: string;
    history: CorrelationHistoryPoint[];
  } {
    const correlations: number[] = [];
    const rollingDates: string[] = [];
    
    for (let i = windowSize; i < returns1.length; i++) {
      const window1 = returns1.slice(i - windowSize, i);
      const window2 = returns2.slice(i - windowSize, i);
      
      const correlation = this.pearsonCorrelation(window1, window2);
      correlations.push(correlation);
      rollingDates.push(dates[i]);
    }
    
    // Calculate rolling volatility of correlations (21-day window)
    const history = correlations.map((corr, idx) => {
      const volWindow = Math.max(0, idx - 20);
      const correlationWindow = correlations.slice(volWindow, idx + 1);
      const volatility = this.calculateStdDev(correlationWindow);
      const regime = this.classifyVolatilityRegime(volatility, volatility);
      
      return {
        date: rollingDates[idx],
        correlation: corr,
        volatility,
        regime
      };
    });
    
    return {
      correlations,
      dates: rollingDates,
      current: correlations[correlations.length - 1],
      current_date: rollingDates[rollingDates.length - 1],
      history
    };
  }
  
  /**
   * Fit GARCH(1,1) model to correlation series with adaptive parameter estimation
   * Returns volatility estimates and forecasts
   */
  private fitGARCH(correlations: number[]): GARCHResults {
    // Demeaned series (remove mean)
    const mean = this.calculateMean(correlations);
    const demeaned = correlations.map(c => c - mean);
    
    // Squared demeaned returns (innovations)
    const squaredInnovations = demeaned.map(d => d * d);
    
    // Estimate GARCH parameters adaptively
    const params = this.garchEstimator.estimateParameters(demeaned, { method: 'auto' });
    
    console.log(`GARCH parameters estimated via ${params.estimationMethod}:`, {
      omega: params.omega,
      alpha: params.alpha,
      beta: params.beta,
      confidence: params.confidence
    });
    
    // Calculate current conditional variance
    const recentInnovation = squaredInnovations[squaredInnovations.length - 1];
    const recentVariance = this.calculateRollingVariance(
      demeaned.slice(-21), // Last 21 observations
      21
    );
    
    const currentVariance = 
      params.omega + 
      params.alpha * recentInnovation + 
      params.beta * recentVariance;
    
    const currentVolatility = Math.sqrt(Math.max(0, currentVariance));
    
    // Long-term unconditional volatility
    const longTermVolatility = Math.sqrt(
      params.omega / (1 - params.alpha - params.beta)
    );
    
    // Volatility forecast function
    const forecastVolatility = (periods: number): number => {
      let variance = currentVariance;
      
      for (let t = 1; t <= periods; t++) {
        // GARCH(1,1) forecast: σ²_{t+h} = ω + (α+β)σ²_{t+h-1}
        variance = params.omega + (params.alpha + params.beta) * variance;
      }
      
      return Math.sqrt(Math.max(0, variance));
    };
    
    return {
      current_volatility: currentVolatility,
      forecast_volatility: forecastVolatility,
      long_term_volatility: longTermVolatility
    };
  }
  
  /**
   * Forecast correlation using mean reversion + GARCH volatility
   */
  private forecastCorrelation(
    current: number,
    longTermMean: number,
    periods: number,
    garchResults: GARCHResults
  ): CorrelationForecast {
    // Mean reversion: E[ρ_t+h] = μ + (ρ_t - μ)e^{-λh}
    const lambda = this.meanReversionSpeed;
    const decay = Math.exp(-lambda * periods / 21); // Normalize to monthly
    const pointForecast = longTermMean + (current - longTermMean) * decay;
    
    // Confidence interval using GARCH volatility forecast
    const forecastStdDev = garchResults.forecast_volatility(periods);
    const zCrit = 1.96; // 95% confidence
    
    return {
      point: this.clamp(pointForecast, -0.99, 0.99),
      lower: this.clamp(pointForecast - zCrit * forecastStdDev, -0.99, 0.99),
      upper: this.clamp(pointForecast + zCrit * forecastStdDev, -0.99, 0.99)
    };
  }
  
  /**
   * Calculate statistics for correlation series
   */
  private calculateStatistics(correlations: number[]): StatisticalMetrics {
    return {
      long_term_mean: this.calculateMean(correlations),
      correlation_volatility: this.calculateStdDev(correlations),
      min: Math.min(...correlations),
      max: Math.max(...correlations),
      median: this.calculateMedian(correlations)
    };
  }
  
  /**
   * Classify correlation volatility regime
   */
  private classifyVolatilityRegime(
    currentVol: number,
    historicalVol: number
  ): 'stable' | 'moderate' | 'unstable' | 'crisis' {
    // Thresholds calibrated from historical equity-bond correlation data
    if (currentVol < 0.03) return 'stable';
    if (currentVol < 0.07) return 'moderate';
    if (currentVol < 0.12) return 'unstable';
    return 'crisis';
  }
  
  /**
   * Estimate probability of correlation breakdown (GFC-like event)
   */
  private estimateBreakdownProbability(
    currentCorr: number,
    currentVol: number,
    longTermMean: number
  ): number {
    // Breakdown = high correlation + high volatility + deviation from mean
    
    // Component 1: Correlation level (0-1 scale)
    const corrScore = Math.max(0, (currentCorr - longTermMean) / (1 - longTermMean));
    
    // Component 2: Volatility level (0-1 scale)
    const volScore = Math.min(1, currentVol / 0.15); // 15% is "high" vol
    
    // Component 3: Rate of change (momentum)
    const momentumScore = volScore;
    
    // Combined probability (weighted average)
    const probability = 
      0.40 * corrScore + 
      0.40 * volScore + 
      0.20 * momentumScore;
    
    return this.clamp(probability, 0, 1);
  }
  
  /**
   * Calculate percentile rank of current correlation
   */
  private calculatePercentileRank(current: number, history: number[]): number {
    const sorted = [...history].sort((a, b) => a - b);
    const rank = sorted.filter(h => h <= current).length;
    return (rank / sorted.length) * 100;
  }
  
  /**
   * Calculate half-life of mean reversion
   */
  private calculateHalfLife(lambda: number): number {
    // Half-life = ln(2) / λ
    // Convert from monthly to daily
    return (Math.log(2) / lambda) * 21; // Assuming 21 trading days per month
  }
  
  /**
   * Generate human-readable insights
   */
  private generateInsights(
    current: number,
    stats: StatisticalMetrics,
    regime: string,
    breakdownProb: number,
    forecasts: Record<string, CorrelationForecast>
  ): {
    interpretation: string;
    warnings: string[];
    recommendations: string[];
  } {
    const warnings: string[] = [];
    const recommendations: string[] = [];
    
    // Interpretation
    let interpretation = `Current correlation (${current.toFixed(3)}) is `;
    if (current > stats.long_term_mean + stats.correlation_volatility) {
      interpretation += `significantly higher than the long-term average (${stats.long_term_mean.toFixed(3)}), `;
      interpretation += `indicating reduced diversification benefits.`;
    } else if (current < stats.long_term_mean - stats.correlation_volatility) {
      interpretation += `significantly lower than the long-term average (${stats.long_term_mean.toFixed(3)}), `;
      interpretation += `indicating enhanced diversification.`;
    } else {
      interpretation += `near the long-term average (${stats.long_term_mean.toFixed(3)}).`;
    }
    
    // Regime warning
    if (regime === 'crisis' || regime === 'unstable') {
      warnings.push(
        `⚠️ Correlation volatility is ${regime === 'crisis' ? 'extremely high (crisis level)' : 'elevated (unstable)'}. ` +
        `Correlations may change rapidly.`
      );
    }
    
    // Breakdown probability warning
    if (breakdownProb > 0.5) {
      warnings.push(
        `🚨 High probability (${(breakdownProb * 100).toFixed(0)}%) of correlation breakdown. ` +
        `Diversification may fail during market stress.`
      );
    } else if (breakdownProb > 0.3) {
      warnings.push(
        `⚠️ Moderate probability (${(breakdownProb * 100).toFixed(0)}%) of correlation spike. ` +
        `Monitor market conditions closely.`
      );
    }
    
    // Forecast-based warnings
    const forecast1y = forecasts.forecast_1y;
    if (forecast1y.point > current + 0.15) {
      warnings.push(
        `📈 Forecast suggests correlation may increase to ${forecast1y.point.toFixed(3)} ` +
        `within 12 months (+${((forecast1y.point - current) * 100).toFixed(0)}%).`
      );
    }
    
    // Recommendations
    if (current > 0.7 || breakdownProb > 0.3) {
      recommendations.push(
        `Consider adding uncorrelated assets (alternatives, commodities, or defensive strategies) ` +
        `to improve portfolio resilience.`
      );
    }
    
    if (regime === 'crisis') {
      recommendations.push(
        `Review portfolios with high growth allocations (>70%). ` +
        `Increased correlation may reduce expected diversification benefits.`
      );
    }
    
    if (current < stats.long_term_mean - 0.1) {
      recommendations.push(
        `Favorable diversification environment. Consider rebalancing or ` +
        `maintaining current allocations to capture lower correlation benefits.`
      );
    }
    
    return {
      interpretation,
      warnings,
      recommendations
    };
  }
  
  // ============================================================
  // UTILITY METHODS
  // ============================================================
  
  private pearsonCorrelation(x: number[], y: number[]): number {
    const n = x.length;
    const meanX = this.calculateMean(x);
    const meanY = this.calculateMean(y);
    
    let numerator = 0;
    let denomX = 0;
    let denomY = 0;
    
    for (let i = 0; i < n; i++) {
      const devX = x[i] - meanX;
      const devY = y[i] - meanY;
      numerator += devX * devY;
      denomX += devX * devX;
      denomY += devY * devY;
    }
    
    const denom = Math.sqrt(denomX * denomY);
    return denom === 0 ? 0 : numerator / denom;
  }
  
  private calculateMean(values: number[]): number {
    if (values.length === 0) return 0;
    return values.reduce((sum, v) => sum + v, 0) / values.length;
  }
  
  private calculateMedian(values: number[]): number {
    if (values.length === 0) return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0
      ? (sorted[mid - 1] + sorted[mid]) / 2
      : sorted[mid];
  }
  
  private calculateStdDev(values: number[]): number {
    if (values.length === 0) return 0;
    const mean = this.calculateMean(values);
    const variance = values.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / values.length;
    return Math.sqrt(variance);
  }
  
  private calculateRollingVariance(values: number[], window: number): number {
    if (values.length === 0) return 0;
    const recentValues = values.slice(-window);
    const mean = this.calculateMean(recentValues);
    return recentValues.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / window;
  }
  
  private clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
  }
}
